import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useToggleLocalMute,
  useAudioInputs,
  useMeetingManager,
} from 'amazon-chime-sdk-component-library-react';
import AudioOptionsMenu from '../AudioOptionsMenu';
import { resolveDeviceId } from '../../utils/deviceUtils';
import { MoreIcon, MicOnIcon, MicOffIcon } from '../MeetingIcons';
import {
  deviceMenuStyle,
  deviceMenuItemStyle,
} from '../../styles/deviceMenuStyles';
import useOutsideClickClose from '../../hooks/useOutsideClickClose';
import {
  micBarsWrapStyle,
  micMainContentStyle,
  buildMicBars,
} from '../../styles/micTesterStyles';
import useResolvedDeviceId from '../../hooks/useResolvedDeviceId';
import useMicLevel from '../../hooks/useMicLevel';
import playSpeakerTestTone from '../../utils/playSpeakerTestTone';

export const AudioOptionsButton = ({
  onAudioInputSelected,
  onAudioOutputSelected,
  initialAudioOutputDeviceId,
}) => {
  const { t } = useTranslation();
  const { muted, toggleMute } = useToggleLocalMute();
  const { devices, selectedDevice } = useAudioInputs();
  const meetingManager = useMeetingManager();
  const [showMenu, setShowMenu] = useState(false);
  const [micMenuDevices, setMicMenuDevices] = useState([]);
  const [speakerMenuDevices, setSpeakerMenuDevices] = useState([]);
  const resolvedSelectedDeviceId = useResolvedDeviceId(selectedDevice);
  const [activeMicDeviceId, setActiveMicDeviceId] = useState(resolvedSelectedDeviceId || '');
  const [activeSpeakerDeviceId, setActiveSpeakerDeviceId] = useState(initialAudioOutputDeviceId || '');
  const menuRef = useRef(null);
  const micLevel = useMicLevel({
    muted,
    deviceId: activeMicDeviceId,
  });
  const micBars = useMemo(() => buildMicBars({ muted, micLevel }), [muted, micLevel]);

  useEffect(() => {
    if (!resolvedSelectedDeviceId) {
      return;
    }

    setActiveMicDeviceId(resolvedSelectedDeviceId);
  }, [resolvedSelectedDeviceId]);

  useEffect(() => {
    setMicMenuDevices(devices || []);
  }, [devices]);

  useEffect(() => {
    setActiveSpeakerDeviceId(initialAudioOutputDeviceId || '');
  }, [initialAudioOutputDeviceId]);

  useEffect(() => {
    if (!showMenu) {
      return () => {};
    }

    let cancelled = false;

    const syncActiveDeviceFromSession = async () => {
      try {
        const av = meetingManager.meetingSession?.audioVideo;
        if (!av) {
          return;
        }

        if (typeof av.listAudioInputDevices === 'function') {
          const liveDevices = await av.listAudioInputDevices();
          if (!cancelled && liveDevices?.length) {
            setMicMenuDevices(liveDevices);
          }
        }

        if (typeof av.listAudioOutputDevices === 'function') {
          const outputDevices = await av.listAudioOutputDevices();
          if (!cancelled) {
            setSpeakerMenuDevices(outputDevices || []);
          }
        }

        if (typeof av.getCurrentAudioInputDevice !== 'function') {
          return;
        }
        const liveDevice = await av.getCurrentAudioInputDevice();
        const liveDeviceId = await resolveDeviceId(liveDevice);
        if (!cancelled && liveDeviceId) {
          setActiveMicDeviceId(liveDeviceId);
        }
      } catch (_error) {
        // Non-fatal.
      }
    };

    syncActiveDeviceFromSession();

    return () => {
      cancelled = true;
    };
  }, [showMenu, meetingManager]);

  useOutsideClickClose({
    isOpen: showMenu,
    onClose: () => setShowMenu(false),
    containerRef: menuRef,
  });

  const handleSelectDevice = async (deviceId) => {
    try {
      await meetingManager.startAudioInputDevice(deviceId);
      setActiveMicDeviceId(deviceId);
      if (typeof onAudioInputSelected === 'function') {
        onAudioInputSelected(deviceId);
      }
    } catch (_e) {
      // Non-fatal.
    }
  };

  const handleSelectSpeaker = async (deviceId) => {
    try {
      const av = meetingManager.meetingSession?.audioVideo;
      if (typeof meetingManager.startAudioOutputDevice === 'function') {
        await meetingManager.startAudioOutputDevice(deviceId);
      } else if (typeof av?.chooseAudioOutputDevice === 'function') {
        await av.chooseAudioOutputDevice(deviceId);
      }

      setActiveSpeakerDeviceId(deviceId);
      if (typeof onAudioOutputSelected === 'function') {
        onAudioOutputSelected(deviceId);
      }
    } catch (_error) {
      // Non-fatal.
    }
  };

  const handleSpeakerTest = async () => {
    await playSpeakerTestTone(activeSpeakerDeviceId || initialAudioOutputDeviceId || '');
  };

  return (
    <div className="pip-menu-wrap" ref={menuRef}>
      <button
        type="button"
        className={`meeting-action-btn mic-tester-btn ${muted ? 'off' : ''}`}
        onClick={toggleMute}
        data-tooltip={muted ? t('VideoChatV2.audio.unmuteMicrophone') : t('VideoChatV2.audio.muteMicrophone')}
        aria-label={muted ? t('VideoChatV2.audio.unmuteMicrophone') : t('VideoChatV2.audio.muteMicrophone')}
      >
        <span style={micMainContentStyle}>
          <span style={micBarsWrapStyle} aria-hidden="true">
            {micBars.map((style, idx) => (
              <span key={idx} style={style} />
            ))}
          </span>
          {muted ? <MicOffIcon /> : <MicOnIcon />}
        </span>
      </button>
      <>
        <button
          type="button"
          className="pip-mode-more"
          onClick={() => setShowMenu((prev) => !prev)}
          data-tooltip={t('VideoChatV2.audio.options')}
          aria-label={t('VideoChatV2.audio.options')}
        >
          <MoreIcon />
        </button>
        {showMenu && (
          <AudioOptionsMenu
            t={t}
            micDevices={micMenuDevices}
            activeMicDeviceId={activeMicDeviceId}
            onSelectMic={handleSelectDevice}
            speakerDevices={speakerMenuDevices}
            activeSpeakerDeviceId={activeSpeakerDeviceId}
            onSelectSpeaker={handleSelectSpeaker}
            onTestSpeaker={handleSpeakerTest}
            menuStyle={deviceMenuStyle}
            getItemStyle={deviceMenuItemStyle}
          />
        )}
      </>
    </div>
  );
};

export const MicButton = AudioOptionsButton;

